import React, { useEffect, useState } from "react";
// UI Framework
import { ReloadOutlined } from "@ant-design/icons";
// Common Components
import { CheckBox } from "src/Components";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import TableTemplate, {
  IExtraTypeUIProps,
} from "src/Tables/TableTemplate/tableTemplate";
import CellWithChart from "src/Tables/TableTemplate/CellWithChart";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Local Components
import { TaskTableAdditional } from "./Additional";
import TaskExcelActions from "./TaskExcelActions";
// Hooks
import { useAppDispatch } from "src/App/hooks";
// Requests
import {
  getLiveIVChart,
  getTaskTable,
  getNetVolumeChart,
  getDistinctBaseInstrument,
  getOpenInterestChart,
  getNetVolume2Chart,
} from "src/Requests/automaticMarketMaking";
// Types
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { ExtraType } from "src/Interfaces/TableInterfaces";
import {
  ITaskTableReport,
  ITaskSearchParam,
  IChartCommonSearchParam,
  IMarketmakingTaskSearchParam,
} from "src/Interfaces/automaticMarketMaking";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { setTaskRow } from "src/Features/AutomaticMarketMaking/automaticMarketMakingSlice";
import { getKeyValueJSX } from "src/Utils/commonJSX";

interface IProps {
  setRefreshFlag: (flag: boolean) => void;
  refreshFlag: boolean;
}

const Tasks: React.FC<IProps> = ({ refreshFlag, setRefreshFlag }) => {
  // local store
  const dispatch = useAppDispatch();
  const [searchParam, setSearchParam] = useState({
    justActive: false,
  } as IMarketmakingTaskSearchParam);
  const [baseInstruments, setBaseInstruments] = useState([] as DropdownItem[]);

  useEffect(() => {
    fetchInstruments();
  }, []);

  function fetchInstruments() {
    getDistinctBaseInstrument()
      .then((resp) => {
        setBaseInstruments(resp);
      })
      .catch(packetErrorHandler);
  }

  const changeHandler = (name: string, value: any) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const onTableRowClick = (row: ITaskTableReport) => {
    dispatch(setTaskRow(row));
  };

  const getExtraTypeUI = ({
    headerName,
    extraType,
    row,
    cellType,
  }: IExtraTypeUIProps<ITaskTableReport>) => {
    switch (extraType) {
      case ExtraType.WithChart:
        return (
          <CellWithChart<ITaskTableReport, IChartCommonSearchParam>
            title={`${row.username} - ${row.optionName}`}
            cellType={headerName === "SnapshotIV" ? "Normal" : "Price"}
            headerName={headerName}
            row={row}
            extraType={extraType}
            chartGetter={(_) => {
              if (headerName === "NetVolume")
                return getNetVolumeChart({
                  taskId: row.id,
                });
              else if (headerName === "NetVolume2") {
                return getNetVolume2Chart({
                  taskId: row.id,
                });
              } else if (headerName === "openinterest") {
                return getOpenInterestChart({
                  taskId: row.id,
                });
              } else
                return getLiveIVChart({
                  taskId: row.id,
                });
            }}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <div className="w-full flex flex-col justify-start items-start gap-6 border border-gray-300 rounded p-3 ">
      <div className="flex flex-wrap justify-between items-end gap-4 w-full pl-20">
        <div className="flex-1 min-w-[240px]">
          <SelectableButtons
            label="نماد پایه"
            name="baseInsId"
            onChange={changeHandler}
            options={baseInstruments}
            value={searchParam.baseInsId}
          />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-4 mr-auto">
          <TaskExcelActions filters={searchParam} onUpdated={() => setRefreshFlag(true)} />
          <div onClick={() => setRefreshFlag(true)} className="cursor-pointer">
            <ReloadOutlined className={refreshFlag ? "animate-spin" : ""} />
          </div>
          <CheckBox
            checked={searchParam.justActive}
            label="نمایش فعال"
            name="justActive"
            onChange={changeHandler}
          />
        </div>
      </div>

      <div className="overflow-auto order-table w-full">
        <TableTemplate<ITaskSearchParam, ITaskTableReport>
          refreshTableTime={5000}
          withSort
          getDataTable={getTaskTable}
          TableUI={({
            reports,
            onAction,
            setSortingType,
            sortingType,
          }: any) => (
            <div className="w-full flex flex-col justify-start items-start gap-2">
              <div className="grid grid-cols-3 gap-4 w-full">
                {getKeyValueJSX(
                  "مجموع معاملات دستی",
                  // Already formatted by the backend as "N0 (P2%)"; it is not a raw
                  // number, so it must not be re-parsed by addSeperatorToInput.
                  reports.totalManual || "-",
                )}
                {getKeyValueJSX(
                  "مجموع معاملات پلتفرم",
                  reports.totalPortfo || "-",
                )}
                {getKeyValueJSX(
                  "مجموع قدرت خرید مورد استفاده",
                  reports.totalUsedBuyingPower || "-",
                )}
              </div>
              <CommonTableUI
                reports={reports}
                onAction={() => {
                  if (!onAction) return;
                  onAction();
                  fetchInstruments();
                }}
                onTableRowClick={onTableRowClick}
                sortingType={sortingType}
                setSortingType={setSortingType}
                AdditionalBody={TaskTableAdditional}
                refreshFlag={refreshFlag}
                withSelect
                tableBoxClassName="max-h-[350px]"
                getExtraTypeUI={getExtraTypeUI}
              />
            </div>
          )}
          searchParameters={searchParam}
        />
      </div>
    </div>
  );
};

export default Tasks;
